/**
 * Header Component - Top navigation bar
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useProjectStore } from '../../stores';
import { useViewStore } from '../../store/viewStore';
import { useAuthStore } from '../../stores/authStore';
import { useSidebarStore } from '../../store/sidebarStore';
import { useMapControlsStore } from '../../store/mapControlsStore';
import { useRezimPrace } from '../../store/roleViewStore';
import { InteractiveDesignDialog } from '../Dialogs/InteractiveDesignDialog';
import { FiberTopologyDialog2 } from '../Dialogs/FiberTopologyDialog2';
import { SettingsDialog } from '../Dialogs/SettingsDialog';
import { LoginDialog } from '../Auth/LoginDialog';
import { AdminPanel } from '../Admin/AdminPanel';
import { SearchBar } from '../Search/SearchBar';
import { Dropdown } from '../Common/Dropdown';
import CreateSnapshotDialog from '../Dialogs/CreateSnapshotDialog';
import SnapshotsDialog from '../Dialogs/SnapshotsDialog';
import SplitterCatalogDialog from '../Dialogs/SplitterCatalogDialog';
import { useSplitterCatalogStore } from '../../stores/splitterCatalogStore';
import './Header.css';

export const Header: React.FC = () => {
  const { currentProject, projects, setCurrentProject, fetchProjects } = useProjectStore();
  const { viewMode, setViewMode } = useViewStore();
  const { user, isAuthenticated, logout, checkAuth, isAdmin, hasModule } = useAuthStore();
  const { toggleSidebar } = useSidebarStore();
  const { addPoleMode, toggleAddPoleMode } = useMapControlsStore();
  const { rezim, nastavRezim, maPrepinac } = useRezimPrace();
  const [showInteractiveDesign, setShowInteractiveDesign] = useState(false);
  const [showFiberTopology2, setShowFiberTopology2] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCreateSnapshot, setShowCreateSnapshot] = useState(false);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [showSplitterCatalog, setShowSplitterCatalog] = useState(false);
  const fetchSplitterTypes = useSplitterCatalogStore((s) => s.fetchTypes);

  const checkAuthCallback = useCallback(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    checkAuthCallback();
  }, [checkAuthCallback]);

  useEffect(() => {
    if (isAuthenticated && projects.length === 0) {
      fetchProjects();
    }
  }, [isAuthenticated, projects.length, fetchProjects]);

  // Načítaj katalóg splitrov (pre výber aj výpočet strát v celej appke)
  useEffect(() => {
    if (isAuthenticated) fetchSplitterTypes();
  }, [isAuthenticated, fetchSplitterTypes]);

  useEffect(() => {
    console.log('[Header] mobileMenuOpen:', mobileMenuOpen);
  }, [mobileMenuOpen]);

  const handleProjectSelect = (project: any) => {
    setCurrentProject(project);
    setMobileMenuOpen(false);
  };

  // Tools dropdown items
  const toolsDropdownItems = [
    {
      label: 'Vláknová Topológia',
      icon: '🔀',
      onClick: () => setShowFiberTopology2(true),
      disabled: !currentProject,
    },
    {
      label: 'Interaktívna Mapa',
      icon: '🎨',
      onClick: () => setShowInteractiveDesign(true),
      disabled: !currentProject,
    },
    {
      label: 'Vytvoriť zálohu',
      icon: '📸',
      onClick: () => setShowCreateSnapshot(true),
      disabled: !currentProject,
    },
    {
      label: 'Zálohy projektu',
      icon: '📦',
      onClick: () => setShowSnapshots(true),
      disabled: !currentProject,
    },
    ...(isAdmin() ? [{
      label: 'Katalóg splitrov',
      icon: '🔀',
      onClick: () => setShowSplitterCatalog(true),
      disabled: false,
    }] : []),
  ];

  console.log('🔧 Header: toolsDropdownItems =', toolsDropdownItems);
  console.log('🔧 Header: currentProject =', currentProject);

  // Settings dropdown items
  const settingsDropdownItems = [
    {
      label: 'Nastavenia',
      icon: '⚙️',
      onClick: () => setShowSettings(true),
      disabled: false,
    },
    ...(isAdmin() ? [{
      label: 'Admin Panel',
      icon: '👤',
      onClick: () => setShowAdminPanel(true),
      disabled: false,
    }] : []),
    {
      label: 'Odhlásiť sa',
      icon: '🚪',
      onClick: () => logout(),
      disabled: false,
    },
  ];

  return (
    <>
      {mobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            top: 60,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
          }}
        />
      )}
      <header className="header">
      <div className="header-left">
        <button
          className="mobile-menu-toggle"
          onClick={() => {
            console.log('[Header] Hamburger clicked! Current state:', mobileMenuOpen);
            setMobileMenuOpen(!mobileMenuOpen);
          }}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </button>
        <h1 className="header-title">GPON Sieť</h1>
        {maPrepinac && rezim === 'pripajanie' && (
          <button
            className="role-view-badge"
            onClick={() => nastavRezim('zvaranie')}
            title="Si v režime pripájania zákazníkov. Klikni pre návrat k zváraniu."
          >
            👤 Pripájanie zákazníkov ✕
          </button>
        )}
        {currentProject && (
          <div className="header-project">
            <span className="header-project-label">Projekt:</span>
            <span className="header-project-name">{currentProject.name}</span>
          </div>
        )}
      </div>
      <div className="header-center">
        {/* Empty center - can be used for breadcrumbs or other content */}
      </div>
      <div className={`header-right ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        {/* Mobile Project Selector & Tools - only in mobile menu */}
        {mobileMenuOpen && (
          <>
            <div className="mobile-project-selector">
              <div className="mobile-project-header">
                <span className="mobile-project-icon">📁</span>
                <span className="mobile-project-label">Projekty</span>
              </div>
              {projects.length > 0 ? (
                <div className="mobile-project-list">
                  {projects.map((project) => (
                    <button
                      key={project.id}
                      className={`mobile-project-item ${currentProject?.id === project.id ? 'active' : ''}`}
                      onClick={() => handleProjectSelect(project)}
                    >
                      <span className="mobile-project-item-icon">
                        {currentProject?.id === project.id ? '✓' : '📁'}
                      </span>
                      <span className="mobile-project-item-name">{project.name}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mobile-no-projects">Žiadne projekty</p>
              )}
            </div>

            <div className="mobile-project-selector">
              <div className="mobile-project-header">
                <span className="mobile-project-icon">🛠️</span>
                <span className="mobile-project-label">Nástroje</span>
              </div>
              <div className="mobile-project-list">
                <button
                  className={`mobile-project-item ${!currentProject ? 'disabled' : ''} ${addPoleMode ? 'active' : ''}`}
                  onClick={() => {
                    if (currentProject) {
                      toggleAddPoleMode();
                      setMobileMenuOpen(false);
                    }
                  }}
                  disabled={!currentProject}
                  style={{
                    backgroundColor: addPoleMode ? '#10b981' : 'transparent',
                    color: addPoleMode ? 'white' : 'inherit',
                  }}
                >
                  <span className="mobile-project-item-icon">{addPoleMode ? '✅' : '📍'}</span>
                  <span className="mobile-project-item-name">
                    {addPoleMode ? 'Pridávanie stĺpikov ZAPNUTÉ' : 'Pridávanie stĺpikov VYPNUTÉ'}
                  </span>
                </button>
                <button
                  className={`mobile-project-item ${!currentProject ? 'disabled' : ''}`}
                  onClick={() => {
                    if (currentProject) {
                      setShowFiberTopology2(true);
                      setMobileMenuOpen(false);
                    }
                  }}
                  disabled={!currentProject}
                >
                  <span className="mobile-project-item-icon">🔀</span>
                  <span className="mobile-project-item-name">Vláknová Topológia</span>
                </button>
                <button
                  className={`mobile-project-item ${!currentProject ? 'disabled' : ''}`}
                  onClick={() => {
                    if (currentProject) {
                      setShowInteractiveDesign(true);
                      setMobileMenuOpen(false);
                    }
                  }}
                  disabled={!currentProject}
                >
                  <span className="mobile-project-item-icon">🎨</span>
                  <span className="mobile-project-item-name">Interaktívna Mapa</span>
                </button>
                {isAdmin() && (
                  <button
                    className="mobile-project-item"
                    onClick={() => {
                      setShowSplitterCatalog(true);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <span className="mobile-project-item-icon">🔀</span>
                    <span className="mobile-project-item-name">Katalóg splitrov</span>
                  </button>
                )}
              </div>
            </div>

            {/* Režim práce — doteraz bol len na desktope (desktop-only), takže
                z mobilu sa nedal prepnúť. Práve technik v teréne ho pritom
                potrebuje najviac, a ten má v ruke telefón. */}
            {maPrepinac && (
              <div className="mobile-project-selector">
                <div className="mobile-project-header">
                  <span className="mobile-project-icon">🧰</span>
                  <span className="mobile-project-label">Čo práve robím</span>
                </div>
                <div className="mobile-project-list">
                  <button
                    className={`mobile-project-item ${rezim === 'zvaranie' ? 'active' : ''}`}
                    onClick={() => {
                      nastavRezim('zvaranie');
                      setMobileMenuOpen(false);
                    }}
                    style={rezim === 'zvaranie'
                      ? { backgroundColor: '#3b82f6', color: 'white' } : undefined}
                  >
                    <span className="mobile-project-item-icon">🔥</span>
                    <span className="mobile-project-item-name">
                      Zváranie{rezim === 'zvaranie' ? ' — zapnuté' : ''}
                    </span>
                  </button>
                  <button
                    className={`mobile-project-item ${rezim === 'pripajanie' ? 'active' : ''}`}
                    onClick={() => {
                      nastavRezim('pripajanie');
                      setMobileMenuOpen(false);
                    }}
                    style={rezim === 'pripajanie'
                      ? { backgroundColor: '#f59e0b', color: 'white' } : undefined}
                  >
                    <span className="mobile-project-item-icon">👤</span>
                    <span className="mobile-project-item-name">
                      Pripájanie{rezim === 'pripajanie' ? ' — zapnuté' : ''}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Mobile Settings & Logout */}
            <div className="mobile-project-selector">
              <div className="mobile-project-header">
                <span className="mobile-project-icon">⚙️</span>
                <span className="mobile-project-label">Nastavenia</span>
              </div>
              <div className="mobile-project-list">
                <button
                  className="mobile-project-item"
                  onClick={() => {
                    setShowSettings(true);
                    setMobileMenuOpen(false);
                  }}
                >
                  <span className="mobile-project-item-icon">⚙️</span>
                  <span className="mobile-project-item-name">Nastavenia</span>
                </button>
                {isAdmin() && (
                  <button
                    className="mobile-project-item"
                    onClick={() => {
                      setShowAdminPanel(true);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <span className="mobile-project-item-icon">👤</span>
                    <span className="mobile-project-item-name">Admin Panel</span>
                  </button>
                )}
                <button
                  className="mobile-project-item"
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  style={{
                    backgroundColor: '#ef4444',
                    color: 'white',
                  }}
                >
                  <span className="mobile-project-item-icon">🚪</span>
                  <span className="mobile-project-item-name">Odhlásiť sa</span>
                </button>
              </div>
            </div>
          </>
        )}

        {/* View mode toggle - Desktop only, hidden on mobile */}
        <div className="view-mode-toggle desktop-only">
          <button
            className={`toggle-button ${viewMode === 'map' ? 'active' : ''}`}
            onClick={() => {
              setViewMode('map');
              setMobileMenuOpen(false);
            }}
          >
            <span>🗺️</span>
            Mapa
          </button>
          {hasModule('customer_management') && (
            <button
              className={`toggle-button ${viewMode === 'customer-search' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('customer-search');
                setMobileMenuOpen(false);
              }}
            >
              <span>🔎</span>
              Nájdi zákazníka
            </button>
          )}
          <button
            className={`toggle-button ${viewMode === 'documentation' ? 'active' : ''}`}
            onClick={() => {
              setViewMode('documentation');
              setMobileMenuOpen(false);
            }}
          >
            <span>📚</span>
            Dokumentácia
          </button>
          {/* Bez modulu by sa po kliknutí vrátilo 403 - tak to ani neponúkame. */}
          {hasModule('customer_management') && (
            <button
              className={`toggle-button ${viewMode === 'customers' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('customers');
                setMobileMenuOpen(false);
              }}
            >
              <span>👥</span>
              Zákazníci
            </button>
          )}
          {hasModule('customer_management') && (
            <button
              className={`toggle-button ${viewMode === 'orders' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('orders');
                setMobileMenuOpen(false);
              }}
            >
              <span>📝</span>
              Objednávky
            </button>
          )}
        </div>

        {/* Režim práce — vidí ho ten, kto má na oboje modul s úrovňou Úprava.
            Pre admina je to zároveň starý „pohľad montážnika". */}
        {maPrepinac && (
          <div className="role-view-toggle desktop-only">
            <button
              className={`toggle-button ${rezim === 'zvaranie' ? 'active' : ''}`}
              onClick={() => {
                nastavRezim('zvaranie');
                setMobileMenuOpen(false);
              }}
              title="Kazeta, zvary a splittre"
            >
              <span>🔥</span>
              Zváranie
            </button>
            <button
              className={`toggle-button ${rezim === 'pripajanie' ? 'active' : ''}`}
              onClick={() => {
                nastavRezim('pripajanie');
                setMobileMenuOpen(false);
              }}
              title="Voľné porty splittrov a pripájanie zákazníkov"
            >
              <span>👤</span>
              Pripájanie
            </button>
          </div>
        )}

        {/* Projekty toggle button - Desktop only */}
        <button
          className="header-button projekty-btn desktop-only"
          onClick={() => {
            toggleSidebar();
            setMobileMenuOpen(false);
          }}
          title="Zobraziť/Skryť projekty"
        >
          <span>📁</span>
          Projekty
        </button>

        {/* Global Search Bar */}
        <SearchBar />

        {/* Tools Dropdown - Desktop only */}
        <div className="desktop-only">
          <Dropdown
            trigger={
              <button className="header-button tools-dropdown-btn">
                <span>🛠️</span>
                Nástroje
                <span className="dropdown-arrow">▼</span>
              </button>
            }
            items={toolsDropdownItems}
            align="right"
          />
        </div>

        {/* Settings Dropdown - Desktop only */}
        {isAuthenticated && (
          <div className="desktop-only">
            <Dropdown
              trigger={
                <button className="header-button settings-dropdown-btn">
                  <span>⚙️</span>
                  Nastavenia
                  <span className="dropdown-arrow">▼</span>
                </button>
              }
              header={
                <>
                  <span>👤</span>
                  <span className="dropdown-header-name" title={user?.username}>{user?.username}</span>
                </>
              }
              items={settingsDropdownItems}
              align="right"
            />
          </div>
        )}

        {/* Login button for non-authenticated users */}
        {!isAuthenticated && (
          <button
            className="header-button login-btn"
            onClick={() => {
              setShowLogin(true);
              setMobileMenuOpen(false);
            }}
            style={{
              background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
              color: 'white',
              border: 'none',
            }}
          >
            <span>🔐</span>
            Prihlásiť sa
          </button>
        )}
      </div>

      <InteractiveDesignDialog
        open={showInteractiveDesign}
        onClose={() => setShowInteractiveDesign(false)}
      />
      <FiberTopologyDialog2
        open={showFiberTopology2}
        onClose={() => setShowFiberTopology2(false)}
      />
      <SettingsDialog
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
      <LoginDialog
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
      />
      <AdminPanel
        isOpen={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
      />
      <CreateSnapshotDialog
        isOpen={showCreateSnapshot}
        onClose={() => setShowCreateSnapshot(false)}
        onConfirm={handleCreateSnapshot}
        projectName={currentProject?.name || ''}
      />
      <SnapshotsDialog
        isOpen={showSnapshots}
        onClose={() => setShowSnapshots(false)}
        projectId={currentProject?.id || 0}
        projectName={currentProject?.name || ''}
        onRestore={handleRestoreSnapshot}
      />
      <SplitterCatalogDialog
        isOpen={showSplitterCatalog}
        onClose={() => setShowSplitterCatalog(false)}
      />
      </header>
    </>
  );

  async function handleCreateSnapshot(description: string) {
    if (!currentProject) return;

    try {
      const response = await fetch(`/api/projects/${currentProject.id}/snapshots/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          backup_type: 'manual',
          description,
        }),
      });

      if (response.ok) {
        alert('✅ Záloha bola úspešne vytvorená!');
      } else {
        const errorData = await response.json();
        console.error('Snapshot creation error:', errorData);
        alert(`❌ Chyba pri vytváraní zálohy: ${errorData.detail || 'Neznáma chyba'}`);
      }
    } catch (error) {
      console.error('Error creating snapshot:', error);
      alert('❌ Chyba pri vytváraní zálohy');
    }
  }

  async function handleRestoreSnapshot(snapshotId: number) {
    if (!currentProject) return;

    try {
      const response = await fetch(`/api/projects/${currentProject.id}/snapshots/${snapshotId}/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        alert('✅ Projekt bol úspešne obnovený zo zálohy!');
        window.location.reload(); // Reload to show restored data
      } else {
        alert('❌ Chyba pri obnovení zo zálohy');
      }
    } catch (error) {
      console.error('Error restoring snapshot:', error);
      alert('❌ Chyba pri obnovení zo zálohy');
    }
  }
};
